import React, { Fragment, memo, useCallback, useMemo } from 'react'
import tw from 'twin.macro'
import dayjs, { Dayjs } from 'dayjs'
import { DateTime } from 'luxon'
import { Form, Formik } from 'formik'
import { useSnackbar } from 'notistack'
import { useSelector } from 'react-redux'
import { Button } from 'src/components/shared/Button'
import { SectionCard } from 'src/components/shared/SectionCard'
import { FormikDatePicker } from 'src/components/shared/form/formik/FormikDatePicker'
import { FormikTimePicker } from 'src/components/shared/form/formik/FormikTimePicker'
import { AdminRescheduleRetryJob } from 'src/constants/actionTypes'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import type { RetryActionJob } from 'src/interfaces/retryConfig'
import { getReschedulingJobId } from 'src/selectors/retryConfig'
import { rescheduleJobValidator } from './RescheduleJobPanel.validator'

const FORM_ID = 'reschedule-job-form'

interface Props {
  configId: string
  /** `null` renders nothing — there is no job selected to reschedule. */
  selectedJob: RetryActionJob | null
  onCancel(): void
}

interface FormValues {
  // Nullable: `FormikDatePicker`/`FormikTimePicker` write `null` via `setFieldValue` when the
  // field is cleared, and the MUI X field is editable (select-all + Delete clears it). A
  // non-nullable type here let a cleared field reach `.toDate()` and throw silently — see
  // `RescheduleJobPanel.validator.ts`.
  newSchedule: Dayjs | null
}

const buildInitialValues = (selectedJob: RetryActionJob | null): FormValues => {
  return {
    newSchedule: dayjs(selectedJob?.scheduledAt),
  }
}

/**
 * Both the date and time pickers target the SAME `newSchedule` field — the pattern
 * `CustomerProductFormContent` already established in this app: MUI X's `DatePicker`/`TimePicker`
 * each preserve the parts of the controlled Dayjs value they do not edit, so picking a date keeps
 * the existing hour/minute and picking a time keeps the existing calendar day. That produces one
 * combined instant without hand-merging two separate field values.
 *
 * There is no `DateTimePicker`/`minutesStep` here on purpose — see the ruling in the task brief:
 * `BasicDatePicker`/`BasicTimePicker` are the only pickers this app has, neither takes a step
 * prop, and both are shared components this task must not modify.
 */
const RescheduleJobPanelComponent = ({ configId, selectedJob, onCancel }: Props) => {
  const reschedulingJobId = useSelector(getReschedulingJobId)
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()
  const snackbar = useSnackbar()

  const isSaving = selectedJob !== null && reschedulingJobId === selectedJob.id

  const initialValues = useMemo(() => {
    return buildInitialValues(selectedJob)
  }, [selectedJob])

  // A floor of "now", not the start of today: unlike `CustomerProductForm`'s all-day
  // entitlement dates, this picker also sets a time, so a job cannot be rescheduled to a past
  // moment earlier today either. Memoized once rather than recomputed every render — the picker
  // only greys out the calendar, so it need not track the clock exactly.
  const minDate = useMemo(() => {
    return dayjs()
  }, [])

  const handleSubmit = useCallback(
    async (values: FormValues) => {
      if (!selectedJob || !values.newSchedule) {
        return
      }

      // Dayjs stays inside the picker components (`AdapterDayjs`); the dispatch boundary
      // converts to Luxon and sends an ISO string. The backend validates `newSchedule` with
      // `@IsDateString` — a `Date` object or a locale string is a 400.
      const newSchedule = DateTime.fromJSDate(values.newSchedule.toDate()).toISO()

      if (!newSchedule) {
        return
      }

      const result = await asyncDispatchWithErrorToast(
        AdminRescheduleRetryJob.request({
          configId,
          retryJobId: selectedJob.id,
          newSchedule,
        }),
      )

      // Task 6's pattern: don't clear the selection on a failed save, so the admin can retry
      // against the same panel instead of losing their picked time.
      if (!result) {
        return
      }

      snackbar?.enqueueSnackbar('Job rescheduled', { variant: 'success' })
      onCancel()
    },
    [asyncDispatchWithErrorToast, configId, onCancel, selectedJob, snackbar],
  )

  if (!selectedJob) {
    return null
  }

  return (
    <Formik<FormValues>
      initialValues={initialValues}
      enableReinitialize
      validationSchema={rescheduleJobValidator}
      onSubmit={handleSubmit}
    >
      <SectionCard
        title="Reschedule job"
        dataTestId="reschedule-job-panel"
        actions={
          <Fragment>
            <Button
              size="small"
              color="grey"
              onClick={onCancel}
              data-testid="reschedule-job-cancel-button"
            >
              Cancel
            </Button>
            <Button
              size="small"
              color="dark"
              type="submit"
              form={FORM_ID}
              loading={isSaving}
              disabled={isSaving}
              data-testid="reschedule-job-save-button"
            >
              Save
            </Button>
          </Fragment>
        }
      >
        <Form id={FORM_ID} data-testid="reschedule-job-form">
          <div css={tw`flex flex-row gap-3`}>
            <FormikDatePicker<FormValues> name="newSchedule" label="Date" minDate={minDate} />
            <FormikTimePicker<FormValues> name="newSchedule" label="Time" />
          </div>
        </Form>
      </SectionCard>
    </Formik>
  )
}

export const RescheduleJobPanel = memo(RescheduleJobPanelComponent)
